import { sendEmailVerification, type User } from "@firebase/auth";
import { useCallback, useEffect, useState } from "react";
import { api, type BillingView, type Me, type Plan } from "../api";
import { FormError } from "../components/Form";
import { Layout, Loading } from "../components/Layout";
import { config } from "../config";
import { errorMessage } from "../messages";
import { Redirect } from "../router";
import { homeFor, useSession } from "../session";
import { formatFileSize, formatIsraeliPhone, formatPrice } from "../validation";
import { BillingSection } from "./Billing";

export function Account() {
  const { session, refresh } = useSession();

  if (session.status === "loading") {
    return <Loading />;
  }
  if (session.status === "signed-out") {
    return <Redirect to="/login" />;
  }
  const { user, me } = session;
  if (!me) {
    return session.error ? (
      <Layout>
        <section className="card">
          <h1>לא הצלחנו לטעון את החשבון</h1>
          <FormError message={session.error} />
          <button type="button" className="button button-primary" onClick={() => void refresh()}>
            ניסיון נוסף
          </button>
        </section>
      </Layout>
    ) : (
      <Loading />
    );
  }
  if (homeFor(me) !== "/account") {
    return <Redirect to={homeFor(me)} />;
  }

  const firstName = me.fullName?.split(" ")[0] ?? "";
  return (
    <Layout wide>
      <div className="account-heading">
        <h1>שלום{firstName ? `, ${firstName}` : ""}</h1>
        <p className="subtitle">כאן בוחרים חבילה, עוקבים אחרי המנוי ומורידים את התוכנה.</p>
      </div>
      {!user.emailVerified && <VerifyEmailBanner user={user} email={me.email} onVerified={refresh} />}
      <PlanSection me={me} />
      <div className="grid-2">
        <DownloadCard activated={me.activated === true} />
        <DetailsCard me={me} verified={user.emailVerified} />
      </div>
      <HelpCard />
    </Layout>
  );
}

function VerifyEmailBanner({ user, email, onVerified }: { user: User; email: string | null; onVerified: () => Promise<void> }) {
  const [state, setState] = useState<"idle" | "sending" | "sent" | "checking">("idle");
  const [message, setMessage] = useState<string | null>(null);

  // Arriving from the link in the email, the address is already verified:
  // pick that up without asking.
  useEffect(() => {
    void onVerified();
  }, [onVerified]);

  const resend = async () => {
    setState("sending");
    setMessage(null);
    try {
      await sendEmailVerification(user, { url: `${location.origin}/account` });
      setState("sent");
    } catch (error) {
      setMessage(errorMessage(error));
      setState("idle");
    }
  };

  const check = async () => {
    setState("checking");
    setMessage(null);
    await onVerified();
    if (!user.emailVerified) {
      setMessage("הכתובת עדיין לא אומתה. לחצו על הקישור באימייל ששלחנו, ואז נסו שוב.");
    }
    setState("idle");
  };

  return (
    <section className="banner" aria-live="polite">
      <div>
        <strong>אמתו את כתובת האימייל</strong>
        <p>
          שלחנו קישור לאימות אל <span dir="ltr">{email}</span>. כדאי לבדוק גם בתיקיית הספאם.
          {state === "sent" && " שלחנו קישור חדש."}
        </p>
        {message && <p className="banner-error">{message}</p>}
      </div>
      <div className="banner-actions">
        <button type="button" className="button button-small" onClick={() => void check()} disabled={state === "checking"}>
          אימתתי
        </button>
        <button
          type="button"
          className="link-button"
          onClick={() => void resend()}
          disabled={state === "sending" || state === "sent"}
        >
          שליחה חוזרת
        </button>
      </div>
    </section>
  );
}

/**
 * Paid plans through Cardcom when billing is open; until then (and if the
 * billing service cannot be reached) the non-binding plan request.
 */
function PlanSection({ me }: { me: Me }) {
  const { token } = useSession();
  const [view, setView] = useState<BillingView | null>(null);
  const [unreachable, setUnreachable] = useState(false);

  const load = useCallback(async () => {
    try {
      setView(await api.billing(await token()));
    } catch {
      setUnreachable(true);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  if (unreachable) {
    return <RequestPlanSection me={me} />;
  }
  if (!view) {
    return (
      <section className="card">
        <Loading />
      </section>
    );
  }
  if (!view.available && !view.subscription) {
    return <RequestPlanSection me={me} />;
  }
  return <BillingSection me={me} view={view} onChange={setView} reload={load} />;
}

/** Before online payment: the customer names a plan and we activate it by hand. */
function RequestPlanSection({ me }: { me: Me }) {
  const { token, refresh } = useSession();
  const [plans, setPlans] = useState<Plan[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [choosing, setChoosing] = useState(false);
  const [busyPlan, setBusyPlan] = useState<string | null>(null);
  const [chooseError, setChooseError] = useState<string | null>(null);

  useEffect(() => {
    api.catalog().then(setPlans, (error: unknown) => setLoadError(errorMessage(error)));
  }, []);

  const choose = async (planKey: string) => {
    setBusyPlan(planKey);
    setChooseError(null);
    try {
      await api.requestPlan(await token(), planKey);
      await refresh();
      setChoosing(false);
    } catch (error) {
      setChooseError(errorMessage(error));
    } finally {
      setBusyPlan(null);
    }
  };

  if (me.credits > 0) {
    return (
      <section className="card">
        <h2>המנוי שלכם</h2>
        <p className="big-number">
          {me.credits} <span>{me.credits === 1 ? "פרק זמין לעיבוד" : "פרקים זמינים לעיבוד"}</span>
        </p>
      </section>
    );
  }

  const requested = plans?.find((plan) => plan.key === me.requestedPlan);
  if (requested && !choosing) {
    return (
      <section className="card card-highlight">
        <h2>בחרתם בחבילת {requested.name}</h2>
        <p>
          FrameFlow נמצאת בבטא פרטית, ואנחנו מפעילים כל חשבון באופן אישי. נחזור אליכם בקרוב
          {me.phone && (
            <>
              {" "}
              לטלפון <span dir="ltr">{formatIsraeliPhone(me.phone)}</span>
            </>
          )}{" "}
          כדי להפעיל את החשבון ולהסדיר את התשלום.
        </p>
        <div className="actions">
          <a className="button button-primary" href={config.whatsappUrl} target="_blank" rel="noopener">
            רוצים לזרז? כתבו לנו בוואטסאפ
          </a>
          <button type="button" className="link-button" onClick={() => setChoosing(true)}>
            שינוי חבילה
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="card">
      <h2>בחירת חבילה</h2>
      <p className="subtitle">הבחירה לא מחייבת: לפני כל חיוב ניצור איתכם קשר להפעלת החשבון.</p>
      {loadError && <FormError message={loadError} />}
      {!plans && !loadError && <Loading />}
      {plans && (
        <div className="plans">
          {plans.map((plan) => (
            <article key={plan.key} className={plan.key === me.requestedPlan ? "plan plan-current" : "plan"}>
              <h3>{plan.name}</h3>
              <p className="price">
                {formatPrice(plan.priceAgorot)}
                <span> לחודש</span>
              </p>
              <ul>
                <li>{plan.episodesPerPeriod} פרקים בחודש</li>
                <li>עד {plan.reelsPerEpisode} רילסים מכל פרק</li>
                <li>פרקים באורך עד {plan.maxEpisodeMinutes} דקות</li>
              </ul>
              <button
                type="button"
                className="button button-primary"
                onClick={() => void choose(plan.key)}
                disabled={busyPlan !== null}
                aria-busy={busyPlan === plan.key}
              >
                {busyPlan === plan.key && <span className="spinner spinner-small" aria-hidden="true" />}
                {plan.key === me.requestedPlan ? "החבילה שבחרתם" : "בחירה"}
              </button>
            </article>
          ))}
        </div>
      )}
      <FormError message={chooseError} />
    </section>
  );
}

/** The Mac installer, for accounts we activated. The API signs a link that works for a few minutes. */
function DownloadCard({ activated }: { activated: boolean }) {
  const { token } = useSession();
  const [busy, setBusy] = useState(false);
  const [started, setStarted] = useState<{ version: string; sizeBytes: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const download = async () => {
    setBusy(true);
    setError(null);
    try {
      const link = await api.desktopDownload(await token());
      setStarted({ version: link.version, sizeBytes: link.sizeBytes });
      // The link answers as an attachment, so the browser downloads it and stays on this page.
      window.location.assign(link.url);
    } catch (failure) {
      setError(errorMessage(failure));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card">
      <h2>FrameFlow למק</h2>
      <p>התוכנה עובדת על מק עם מעבד Apple Silicon (M1 ואילך) ומערכת macOS 14 ומעלה. העריכה נעשית על המחשב שלכם, והחומרים נשארים אצלכם.</p>
      {activated ? (
        <>
          <div className="actions">
            <button type="button" className="button button-primary" onClick={() => void download()} disabled={busy} aria-busy={busy}>
              {busy && <span className="spinner spinner-small" aria-hidden="true" />}
              הורדת התוכנה
            </button>
          </div>
          {started && (
            <p className="muted" role="status">
              ההורדה התחילה: FrameFlow {started.version}, {formatFileSize(started.sizeBytes)}. כשהיא מסתיימת, פתחו את הקובץ וגררו את
              FrameFlow לתיקיית היישומים (Applications). משם התוכנה מתעדכנת מעצמה.
            </p>
          )}
          <FormError message={error} />
        </>
      ) : (
        <p className="muted">קישור ההורדה יופיע כאן אחרי הפעלת החשבון.</p>
      )}
    </section>
  );
}

function DetailsCard({ me, verified }: { me: Me; verified: boolean }) {
  return (
    <section className="card">
      <h2>הפרטים שלכם</h2>
      <dl className="details">
        <dt>שם</dt>
        <dd>{me.fullName}</dd>
        <dt>אימייל</dt>
        <dd>
          <span dir="ltr">{me.email}</span> {verified ? <span className="badge badge-ok">מאומת</span> : <span className="badge">לא מאומת</span>}
        </dd>
        <dt>טלפון</dt>
        <dd dir="ltr">{me.phone ? formatIsraeliPhone(me.phone) : "—"}</dd>
        <dt>עדכונים ודיוור</dt>
        <dd>{me.marketingOptIn ? "כן" : "לא"}</dd>
      </dl>
    </section>
  );
}

function HelpCard() {
  return (
    <section className="card help">
      <div>
        <h2>צריכים עזרה?</h2>
        <p>אנחנו זמינים בוואטסאפ לכל שאלה על ההרשמה, על החבילות ועל התוכנה.</p>
      </div>
      <a className="button button-secondary" href={config.whatsappUrl} target="_blank" rel="noopener">
        דברו איתנו בוואטסאפ
      </a>
    </section>
  );
}
